import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Box,
  Button,
  Card,
  CardActions,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useContext, useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { InternBrandStyleTypography_brand$key } from "./__generated__/InternBrandStyleTypography_brand.graphql";
import SnackbarContext from "../../../../contexts/SnackbarContext";
import useBrandAppConfigUpdate2Mutation from "../../../../mutations/useBrandAppConfigUpdate2Mutation";
import InternBrandStyleTypographyItem from "./InternBrandStyleTypographyItem";
import InternFont from "./InternFont";
import { getUniqueFontFamilies } from "./helpers";
import { Font } from "./types";

const brandFragment = graphql`
  fragment InternBrandStyleTypography_brand on BrandType {
    id
    internStyle {
      fonts
      fontFaces {
        name
        bold {
          url
          fontWeight
          fontFace
        }
        regular {
          url
          fontWeight
          fontFace
        }
      }
    }
    appConfig {
      id
      buildTheme {
        id
        fonts {
          name
          type
        }
      }
    }
  }
`;

type InternBrandStyleTypographyProps = {
  brand: InternBrandStyleTypography_brand$key;
  onFontSelect?: (fonts: Font[]) => void;
};

const InternBrandStyleTypography = ({
  brand: brandKey,
  onFontSelect,
}: InternBrandStyleTypographyProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [updateBrandAppConfig, isMutationInFlight] =
    useBrandAppConfigUpdate2Mutation();
  const snackbarContext = useContext(SnackbarContext);
  const [selectedFonts, setSelectedFonts] = useState<Font[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set([0])
  );

  const [, setNewStyleElement] = useState<HTMLStyleElement | null>(null);

  const fonts = useMemo(() => {
    if (!brand.internStyle?.fontFaces) return [];
    return getUniqueFontFamilies(
      brand.internStyle.fontFaces.map((font) => font.bold.fontFace)
    );
  }, [brand.internStyle?.fontFaces]);

  useEffect(() => {
    // TODO: also load saved fonts
    if (!fonts) return;
    const styleElement = document.createElement("style");
    styleElement.appendChild(document.createTextNode(fonts.join("")));
    document.head.appendChild(styleElement);
    setNewStyleElement(styleElement);

    return () => {
      if (!styleElement) return;
      styleElement.remove();
      setNewStyleElement((element) => {
        if (element === styleElement) {
          return null;
        }
        return element;
      });
    };
  }, [fonts]);

  const availableFonts: Font[] = useMemo(() => {
    // Convert the fonts array to Font objects
    return fonts.map((font: string, index: number) => ({
      name: font,
      type: index === 0 ? "HEADING_REGULAR" : "BODY_REGULAR",
    }));
  }, [fonts]);

  const savedFonts: Font[] | null = useMemo(() => {
    if (!brand.appConfig?.buildTheme?.fonts) return null;
    return [...brand.appConfig.buildTheme.fonts];
  }, [brand.appConfig?.buildTheme?.fonts]);

  useEffect(() => {
    if (selectedFonts.length === 0 && availableFonts.length > 0) {
      const firstFont = availableFonts[0];
      const secondFont = availableFonts.length > 1 ? availableFonts[1] : null;

      if (secondFont) {
        // Select first two fonts in order (header and body)
        setSelectedFonts([firstFont, secondFont]);
        setSelectedIndices(new Set([0, 1]));
      } else {
        // Only one font available
        setSelectedFonts([firstFont]);
        setSelectedIndices(new Set([0]));
      }
    }
  }, [availableFonts, selectedFonts.length]);

  const hasChanges = useMemo(() => {
    if (!selectedFonts || !savedFonts) {
      return selectedFonts.length > 0;
    }

    if (selectedFonts.length !== savedFonts.length) {
      return true;
    }

    return JSON.stringify(selectedFonts) !== JSON.stringify(savedFonts);
  }, [selectedFonts, savedFonts]);

  const handleFontSelect = (font: Font, index: number) => {
    const newSelectedIndices = new Set(selectedIndices);

    if (newSelectedIndices.has(index)) {
      // If clicking on an already selected font, deselect it only if we have more than 1 font selected
      if (newSelectedIndices.size > 1) {
        newSelectedIndices.delete(index);
      }
    } else {
      // If clicking on a new font, add it only if we have less than 2 fonts selected
      if (newSelectedIndices.size < 2) {
        newSelectedIndices.add(index);
      }
    }

    setSelectedIndices(newSelectedIndices);

    // Preserve the order of selection by tracking when fonts were added
    const newSelectedFonts: Font[] = [];

    // If this is a new selection, add it to the end
    if (!selectedIndices.has(index) && newSelectedIndices.has(index)) {
      newSelectedFonts.push(...selectedFonts);
      newSelectedFonts.push(font);
    } else if (selectedIndices.has(index) && !newSelectedIndices.has(index)) {
      // Remove the font from selection order
      newSelectedFonts.push(
        ...selectedFonts.filter((f) => f.name !== font.name)
      );
    } else {
      // No change in selection, keep current order
      newSelectedFonts.push(...selectedFonts);
    }

    setSelectedFonts(newSelectedFonts);
    onFontSelect?.(newSelectedFonts);
  };

  const handleSave = () => {
    if (selectedFonts.length === 0) return;

    const fontFaces = brand.internStyle?.fontFaces.filter((font) =>
      selectedFonts.some((f) => f.name === font.name)
    );
    if (!fontFaces || fontFaces.length !== selectedFonts.length) {
      snackbarContext?.openSnackbar(
        "Failed to map back to font faces",
        "error"
      );
      return;
    }

    updateBrandAppConfig({
      id: brand.appConfig?.id,
      buildTheme: {
        id: brand.appConfig?.buildTheme?.id,
        fontFaces: fontFaces,
      },
    });
  };

  return (
    <Card
      sx={{
        p: 2,
        width: "100%",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" mb={2} fontWeight={600}>
        Typography
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={4}
        sx={{ width: "100%", minWidth: 0, flex: 1 }}
      >
        {/* Left side - Font list */}
        <Box
          sx={{
            flex: { sm: "0 0 270px" },
            width: { xs: "100%", sm: 270 },
            maxWidth: "100%",
            position: "relative",
            minHeight: "180px",
          }}
        >
          <Box
            p={1}
            sx={{
              overflowY: "auto",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            {availableFonts?.map((font: Font, index: number) => {
              // Find the selection order for this font
              const selectionOrder = selectedFonts.findIndex(
                (f) => f.name === font.name
              );
              return (
                <InternBrandStyleTypographyItem
                  key={index}
                  index={index}
                  font={font}
                  selectable={true}
                  isSelected={selectedIndices.has(index)}
                  onClick={() => handleFontSelect(font, index)}
                  size="small"
                  selectionOrder={
                    selectionOrder >= 0 ? selectionOrder : undefined
                  }
                />
              );
            })}
          </Box>
        </Box>

        {/* Right side - Selected font preview */}
        <Stack
          sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: "100%" }}
          alignItems="start"
          direction="row"
          spacing={2}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            {savedFonts && (
              <Box
                sx={{
                  width: 200,
                  minHeight: 60,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "center",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  p: 1,
                  gap: 1,
                }}
              >
                {savedFonts.map((font: Font, index: number) => (
                  <InternFont
                    key={index}
                    font={font}
                    size={index === 0 ? "large" : "small"}
                  />
                ))}
              </Box>
            )}

            {selectedFonts.length > 0 && (
              <>
                <ArrowDownwardIcon fontSize="small" />
                <Stack
                  direction="column"
                  gap={1}
                  sx={{
                    width: 200,
                    minHeight: 60,
                    alignItems: "stretch",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    p: 1,
                  }}
                >
                  {selectedFonts.map((font: Font, index: number) => (
                    <InternFont
                      key={index}
                      font={font}
                      size={index === 0 ? "large" : "small"}
                    />
                  ))}
                  {selectedFonts.length === 1 && (
                    <InternFont font={selectedFonts[0]} size="small" />
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          mt: "auto",
          borderTop: "1px solid #DBDCDD",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={
            !hasChanges || isMutationInFlight || selectedFonts.length === 0
          }
          onClick={handleSave}
        >
          {isMutationInFlight ? "Saving..." : "Save"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InternBrandStyleTypography;
